import { useEffect, useId, useRef, useState } from "react";
import {
  ANSWERED_OUTCOMES,
  WHATSAPP_LEAD_BLOCKS,
  type AppointmentView,
  type CallView,
  type LeadCardResponse,
  type LeadWriteResponse,
  type PhoneSlot,
  type TaskView,
  type WhatsAppTemplateKeyCode,
} from "@gioia365/contracts";
import { WHATSAPP_SUGGESTED_AFTER } from "@gioia365/domain/whatsapp";
import type { MessageKey } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import { RadioGroup } from "../../design/components/RadioGroup";
import { Reason } from "../../design/components/Reason";
import { Select } from "../../design/components/Select";
import { TextArea } from "../../design/components/TextArea";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { formatNumericDateTime } from "../../i18n/format";
import { useT } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { useCallSession, type CallSession, type OutcomeSaved } from "../../telephony/call-session";
import type { OutcomeInput } from "../../telephony/telephony-queries";
import { whatsAppTemplatesQuery } from "../../whatsapp/api";
import { WhatsAppDialog } from "../../whatsapp/WhatsAppDialog";
import { unavailableReasonText } from "../../whatsapp/whatsapp-texts";
import { isCardLevelError, leadStatusesQuery, useLeadView, type LeadCardWrites } from "../lead-queries";
import { BookAppointmentDialog } from "../../appointments/BookAppointmentDialog";
import { CallbackDialog, callsBlocked, canCallBack, canSetCallback, completedByCall } from "./CallbackDialog";
import { statusEdit } from "./card-fields";

// L'esito della chiamata e i passi dopo l'esito, nella postazione (piano M4, Task 13, D7; mock-up `scheda-lead.html`;
// questione 160). L'esito si sceglie in due passi: «Richiama ora», «Risposta», «Nessuna risposta», «Numero errato» (l'ordine
// del product owner del 25 settembre 2026) e, dopo «Risposta», «Com'è andata?» con le sei scelte. Poi soltanto i passi che
// esistono già: gli appunti (obbligatori soltanto con «Altro»), lo status («Vuoi modificare lo status?», obbligatorio soltanto
// con lo status di partenza dopo almeno due minuti di conversazione, con «Metti «Numero Errato»» e «Metti «Non vuole essere
// chiamato»»), e con «Numero errato» la ricerca del salone e la correzione del numero. La prenotazione arriva con la sua
// milestone, senza pulsanti finti.
//
// I richiami (piano M5, Task 17; mock-up `scheda-lead.html`, product owner del 27 settembre 2026): dopo «Richiamare» il passo
// obbligatorio «Scegli quando richiamare» (`callback`), che apre da solo la finestra «Richiama dopo» (`CallbackDialog.tsx`)
// quando si sceglie l'esito; l'esito si salva quando si sceglie l'orario, così prima si può ancora cambiare. Sotto i passi, per
// ogni esito, «Richiama dopo», che non c'è quando le chiamate al Lead sono ferme e sparisce quando il richiamo è fissato in
// questa chiamata. Il passo fatto sta nella sessione della chiamata (`doneSteps`), così resta fatto anche lasciando la scheda;
// finché non è fatto la postazione non si chiude, e nel Power Dialer «Lead successivo» e «Interrompi» restano spenti (ruling Q8,
// Q46). Chi non può fissare richiami (`tasks.create` o la modifica del Lead), o spostare quello che il Lead ha già, non ha né il
// passo né il pulsante (questione 326), e nemmeno chi chiama un Lead con le chiamate ferme (questione 324). Il richiamo fissato
// l'ultima volta prima della chiamata lo completa la chiamata stessa (ruling Q55), anche se la scheda letta al riaggancio non lo sa
// ancora: per il passo e per la finestra non c'è già più, così il passo non si spegne e riaccende quando la scheda si aggiorna.
//
// L'esito si salva prima di ogni passo che cambia il Lead (nella Timeline la chiamata viene prima dello status) e con
// «Chiudi la chiamata», che chiude la postazione soltanto quando il server ha confermato l'esito (e lo status in corso di
// salvataggio) e nessun passo obbligatorio resta da fare (`stepsDone` della sessione). I passi obbligatori li dice una
// funzione sola, `pendingRequiredSteps`: un passo nuovo (M5: «Scegli quando richiamare») si aggiunge a `AFTER_OUTCOME_STEPS`.
// Lo status è obbligatorio soltanto quando la conversazione è durata almeno due minuti (`STATUS_REQUIRED_AFTER_SECONDS`) e il
// Lead ha ancora lo status di partenza, letto dalla scheda stessa (`lead.status.isDefault`), e soltanto per chi può modificare
// il Lead; più breve, o con una durata che non si sa, la postazione si chiude anche senza cambiarlo (decisioni del product
// owner del 5 ottobre 2026: pochi blocchi, soltanto dove la situazione è chiara). Se la lista degli status non si carica, il
// passo dice perché e offre «Riprova» invece di una tendina che non fa nulla. Di una chiamata ripresa con «Manca l'esito…»
// non si sa quale telefono fosse: niente «Richiama ora» né «Correggi il numero», che potrebbero prendere un altro numero del
// Lead.
//
// «Invia WhatsApp» (piano M6, R1, Task 4b; ruling A4 e B7; mock-up `mockup.js`, `FLOWS`): dopo «Nessuna risposta», «Richiamare»
// e «Non interessato» un passo facoltativo propone il modello di `WHATSAPP_SUGGESTED_AFTER` («Dopo chiamata senza risposta»,
// «Conferma richiamo», «Dopo la chiamata con risposta») e apre la finestra del WhatsApp a mano (`WhatsAppDialog`) con la chiamata;
// prima salva l'esito, come ogni passo che agisce fuori dalla postazione. Non c'è con «Richiama ora» (stai già richiamando) e
// sparisce quando in questa chiamata un messaggio è preparato (`markStepDone("whatsapp")`, anche per un messaggio riusato), così
// non si ripropone nella stessa chiamata (ACCEPTANCE: «non si ripropongono se nella stessa chiamata sono già stati fatti»). Spento
// per un motivo del Lead (status che blocca, nessun cellulare, «Non inviare WhatsApp») il passo resta, con il pulsante spento e il
// motivo; per un motivo di chi guarda (senza «Mandare WhatsApp», un Lead che non modifica) non c'è, come i richiami per chi non li
// può fissare (questione 326). Con gli altri esiti «Invia WhatsApp» sta sotto i passi, accanto a «Richiama dopo» (il «Puoi anche»
// del mock-up; ACCEPTANCE: dopo l'esito il Commerciale può scegliere anche «Invia WhatsApp»): la stessa finestra con la chiamata e
// il primo modello dell'azienda, con le stesse regole del passo. Uno solo dei due c'è: quando il passo propone il modello, il
// pulsante in fondo sarebbe un doppione con lo stesso nome.

type CallOutcome = OutcomeInput["outcome"];
type AnsweredOutcome = (typeof ANSWERED_OUTCOMES)[number];

/**
 * Da quanti secondi di conversazione lo status di partenza («No Type») va cambiato prima di chiudere (decisione del product
 * owner del 5 ottobre 2026: «quando magari la telefonata dura "almeno 2 minuti"»). Una costante finché «Esiti e percorsi»
 * non lo rende un'impostazione (questione 385). Va in minuti interi, almeno due: i testi la dicono «{minutes} minuti», al
 * plurale (`callDesk.reasonStatus`, `callDesk.barMissingStatus`), e un test lo controlla.
 */
export const STATUS_REQUIRED_AFTER_SECONDS = 120;

/**
 * I fatti da cui dipendono i passi dopo l'esito: l'esito, lo status del Lead (quello di partenza o no), se chi guarda può
 * modificare il Lead, quanto è durata la conversazione e se si sa quale telefono è stato chiamato.
 */
export type StepFacts = {
  outcome: CallOutcome | null;
  answeredOutcome: AnsweredOutcome | null;
  leadStatusIsDefault: boolean;
  canUpdateLead: boolean;
  /** I secondi di conversazione, dalla risposta del Lead alla fine (`talkSecondsOf`); `null` se non si sanno. */
  talkSeconds: number | null;
  phoneKnown: boolean;
  /**
   * Chi guarda può fissare un richiamo su questo Lead, oppure spostare quello che il Lead avrà ancora dopo la chiamata, e le
   * chiamate al Lead non sono ferme (piano M5, Task 17): senza, né il passo dopo «Richiamare» né «Richiama dopo».
   */
  canSetCallback: boolean;
  /**
   * La scheda propone il WhatsApp per questo Lead (piano M6, R1, Task 4b): acceso, oppure spento per un motivo del Lead, che il passo
   * dice; un motivo di chi guarda, o una scheda che non dice niente del WhatsApp, non lo propone.
   */
  canOfferWhatsApp: boolean;
  /** In questa chiamata un messaggio è già preparato: il passo «Invia WhatsApp» non si ripropone. */
  whatsAppPrepared: boolean;
  /** «Richiama ora»: l'esito è «Nessuna risposta» e parte subito un'altra chiamata, niente WhatsApp. */
  callingAgain: boolean;
  /** Chi guarda può prenotare disco e demo su questo Lead (piano M6, R7, Task 6): senza, il passo della prenotazione non c'è. */
  canBook?: boolean;
  /** Il WhatsApp si può mandare adesso a questo Lead: dopo una disco o una demo prenotata la conferma è obbligatoria (TEL-20). */
  whatsAppAvailable?: boolean;
};

/** L'esito è «Disco prenotata» o «Demo prenotata»: il tipo da prenotare, altrimenti `null`. */
export function bookingKindOf(facts: Pick<StepFacts, "outcome" | "answeredOutcome">): "disco" | "demo" | null {
  if (facts.outcome !== "answered") return null;
  return facts.answeredOutcome === "disco_booked" ? "disco" : facts.answeredOutcome === "demo_booked" ? "demo" : null;
}

/** Un passo dopo l'esito: quando c'è e quando è obbligatorio (finché lo è, la postazione non si chiude). */
export type AfterOutcomeStep = { id: string; appliesTo(facts: StepFacts): boolean; required(facts: StepFacts): boolean };

/**
 * Il modello WhatsApp proposto dopo l'esito (`WHATSAPP_SUGGESTED_AFTER` del dominio, ruling B7): «Nessuna risposta», poi dopo
 * «Risposta» «Richiamare» e «Non interessato»; gli altri esiti nessuno.
 */
export function suggestedWhatsApp(facts: Pick<StepFacts, "outcome" | "answeredOutcome">): WhatsAppTemplateKeyCode | null {
  const outcome = facts.outcome === "answered" ? facts.answeredOutcome : facts.outcome;
  return outcome === null ? null : (WHATSAPP_SUGGESTED_AFTER[outcome] ?? null);
}

/** Lo status si chiede dopo «Numero errato» e dopo ogni risposta; con «Nessuna risposta» no (mock-up, `FLOWS`). */
const hasStatusStep = (facts: StepFacts) => facts.outcome === "wrong_number" || (facts.outcome === "answered" && facts.answeredOutcome !== null);

export const AFTER_OUTCOME_STEPS: readonly AfterOutcomeStep[] = [
  // «Scegli quando richiamare» dopo «Richiamare» (piano M5, Task 17; obbligatorio, confermato dal product owner il 5 ottobre
  // 2026): fatto quando il richiamo è fissato in questa chiamata.
  {
    id: "callback",
    appliesTo: (facts) => facts.outcome === "answered" && facts.answeredOutcome === "call_back" && facts.canSetCallback,
    required: () => true,
  },
  // «Prenota» dopo «Disco prenotata» o «Demo prenotata» (TEL-20, piano M6, R7, Task 6): obbligatorio, fatto quando l'appuntamento
  // è prenotato in questa chiamata.
  { id: "booking", appliesTo: (facts) => bookingKindOf(facts) !== null && facts.canBook === true, required: () => true },
  { id: "search", appliesTo: (facts) => facts.outcome === "wrong_number", required: () => false },
  { id: "fix_phone", appliesTo: (facts) => facts.outcome === "wrong_number" && facts.phoneKnown, required: () => false },
  // «Invia WhatsApp» (piano M6, R1, Task 4b): facoltativo, dopo il richiamo e prima dello status, come in `FLOWS`.
  {
    id: "whatsapp",
    appliesTo: (facts) => facts.canOfferWhatsApp && !facts.whatsAppPrepared && !facts.callingAgain && suggestedWhatsApp(facts) !== null,
    // La conferma di una disco o di una demo è obbligatoria soltanto se il Lead la può ricevere adesso (TEL-20).
    required: (facts) => bookingKindOf(facts) !== null && facts.whatsAppAvailable === true,
  },
  // Lo status di partenza blocca soltanto dopo una conversazione lunga e con la durata nota: una durata che non si sa non blocca.
  {
    id: "status",
    appliesTo: hasStatusStep,
    required: (facts) => facts.leadStatusIsDefault && facts.canUpdateLead && longTalk(facts.talkSeconds),
  },
];

/** La conversazione è durata almeno `STATUS_REQUIRED_AFTER_SECONDS`; una durata che non si sa non conta come lunga. */
function longTalk(talkSeconds: number | null): boolean {
  return talkSeconds !== null && talkSeconds >= STATUS_REQUIRED_AFTER_SECONDS;
}

/**
 * La durata della conversazione per la regola dello status: quella del server se la conosce già (la «Durata» della Timeline
 * e di «Conversazioni»), dalla chiamata salvata con l'esito o da quella della postazione; altrimenti quella misurata dal
 * browser dalla risposta del Lead alla fine. `null` se nessuno dei due la sa: allora lo status non blocca.
 */
export function talkSecondsOf(session: Extract<CallSession, { phase: "outcome" }>, saved: CallView | null = null): number | null {
  return saved?.durationSeconds ?? session.call.durationSeconds ?? session.talkSeconds;
}

/** I passi obbligatori dopo l'esito ancora da fare: `done` sono quelli già fatti (lo status è fatto quando cambia). */
export function pendingRequiredSteps(facts: StepFacts, done: ReadonlySet<string> = new Set()): string[] {
  return AFTER_OUTCOME_STEPS.filter((step) => step.appliesTo(facts) && step.required(facts) && !done.has(step.id)).map((step) => step.id);
}

/** Lo status rapido di un esito, dalla chiave stabile degli status di partenza. */
function quickStatusKey(facts: StepFacts): string | null {
  if (facts.outcome === "wrong_number") return "numero_errato";
  if (facts.outcome === "answered" && facts.answeredOutcome === "do_not_call") return "non_vuole_essere_chiamato";
  return null;
}

const FIRST_STEP: ReadonlyArray<{ value: Exclude<CallOutcome, never>; label: MessageKey }> = [
  { value: "answered", label: "callDesk.answered" },
  { value: "no_answer", label: "callDesk.noAnswer" },
  { value: "wrong_number", label: "callDesk.wrongNumber" },
];

/** L'id della riga dei dati di un posto del telefono (`LeadDataBlock`), per «Correggi il numero». */
function phoneFieldId(slot: PhoneSlot): string {
  if (slot.holder === "second") return "lead-second-mobile";
  return slot.kind === "mobile" ? "lead-system-mobile" : "lead-system-landline";
}

/** Apre il numero chiamato nei dati della scheda, con la casella della modifica. */
function openPhoneField(slot: PhoneSlot) {
  const row = document.getElementById(`${phoneFieldId(slot)}-label`)?.closest(".g-lead-data__row");
  const details = row?.closest("details");
  if (details) details.open = true;
  const button = row?.querySelector<HTMLButtonElement>("button.g-lead-data__value");
  if (!button) return;
  if (typeof button.scrollIntoView === "function") button.scrollIntoView({ block: "center", behavior: "smooth" });
  button.click();
}

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  session: Extract<CallSession, { phase: "outcome" }>;
  writes: LeadCardWrites;
  onCardError: (error: unknown) => void;
  /** Il fuso dell'azienda, per gli orari dei richiami. */
  timeZone: string;
};

export function OutcomeSteps({ tab, card, session, writes, onCardError, timeZone }: Props) {
  const t = useT();
  const toast = useToast();
  const api = useCallSession();
  const access = useAccess();
  const view = useLeadView();
  const callbackQuestionId = useId();
  const bookingQuestionId = useId();
  const statuses = useQuery(leadStatusesQuery(tab));
  const notesId = useId();
  const statusQuestionId = useId();
  const statusSelectId = useId();
  const whatsAppQuestionId = useId();
  const whatsAppReasonId = useId();
  const whatsAppLaterReasonId = useId();
  const { save } = session;
  const { lead } = card;
  // La scelta: dal server quando l'esito è salvato, dal salvataggio in corso, altrimenti quella della persona, che riparte
  // dall'ultimo invio quando la postazione torna nella pagina (la scheda lasciata e riaperta durante un salvataggio), oppure
  // dalla bozza che la sessione della chiamata ricorda (la scheda lasciata e riaperta prima di salvare).
  const sent = save.state === "saving" || save.state === "failed" ? save.input : null;
  const kept = sent === null ? api.draftOf(session.call.id) : null;
  const [draft, setDraft] = useState<{ outcome: CallOutcome | null; answeredOutcome: AnsweredOutcome | null }>(() => ({
    outcome: sent?.outcome ?? kept?.outcome ?? null,
    answeredOutcome: sent?.answeredOutcome ?? kept?.answeredOutcome ?? null,
  }));
  const [note, setNote] = useState(() => sent?.note ?? kept?.note ?? "");
  const { keepDraft } = api;
  const callId = session.call.id;
  useEffect(() => {
    keepDraft(callId, { ...draft, note });
  }, [keepDraft, callId, draft, note]);
  const [problem, setProblem] = useState<MessageKey | null>(null);
  // La finestra «Richiama dopo» aperta, e il richiamo fissato in questa chiamata (per «Richiamo fissato per …»).
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [fixed, setFixed] = useState<TaskView | null>(null);
  // La finestra «Prenota disco o demo» aperta dal passo, e l'appuntamento prenotato in questa chiamata.
  const [bookingOpen, setBookingOpen] = useState(false);
  const [booked, setBooked] = useState<AppointmentView | null>(null);
  // La finestra del WhatsApp aperta (dal passo o da sotto i passi), con il modello proposto quando si è aperta (senza: il primo).
  const [whatsAppOpen, setWhatsAppOpen] = useState<{ template: WhatsAppTemplateKeyCode | undefined } | null>(null);
  // «Richiama ora» premuto in questa postazione.
  const [callingAgain, setCallingAgain] = useState(false);
  // Lo status scelto nella tendina vale per lo status di adesso: cambiato lo status (anche da «Metti «…»»), la tendina lo
  // segue, così «Salva lo status» non lo riporta indietro.
  const [statusDraft, setStatusDraft] = useState<{ from: string; to: string } | null>(null);
  const chosenStatus = statusDraft !== null && statusDraft.from === lead.status.id ? statusDraft.to : lead.status.id;
  // Il cambio di status ancora in volo: «Chiudi la chiamata» aspetta che il server lo confermi.
  const statusWrite = useRef<Promise<LeadWriteResponse | null> | null>(null);
  const phone = session.target.phone;
  const locked = save.state === "saved" || save.state === "saving";
  const chosen =
    save.state === "saved"
      ? { outcome: save.call.outcome, answeredOutcome: save.call.answeredOutcome }
      : save.state === "saving"
        ? { outcome: save.input.outcome, answeredOutcome: save.input.answeredOutcome }
        : draft;

  const choices = statuses.data ?? [];
  // La scheda come sarà quando il server chiude la chiamata: senza il richiamo che la chiamata completa (ruling Q55).
  const cardAfterCall = card.openTask && completedByCall(card.openTask, session.call) ? { ...card, openTask: null } : card;
  const done = new Set(api.doneSteps);
  const callbackDone = done.has("callback");
  const bookingDone = done.has("booking");
  const facts: StepFacts = {
    outcome: chosen.outcome,
    answeredOutcome: chosen.answeredOutcome,
    leadStatusIsDefault: lead.status.isDefault,
    canUpdateLead: card.permissions.canUpdate,
    talkSeconds: talkSecondsOf(session, save.state === "saved" ? save.call : null),
    phoneKnown: phone !== null,
    // Fatto il richiamo, il passo resta (lo dice fatto) anche se il richiamo appena fissato non è fra quelli che si spostano.
    canSetCallback: callbackDone || (!callsBlocked(card) && canCallBack(access, cardAfterCall, view)),
    canOfferWhatsApp: card.whatsapp.available || WHATSAPP_LEAD_BLOCKS.some((reason) => reason === card.whatsapp.reason),
    whatsAppPrepared: done.has("whatsapp"),
    canBook: bookingDone || canSetCallback(access, card, view),
    whatsAppAvailable: card.whatsapp.available,
    // Anche ripresa durante il salvataggio (la scheda lasciata e riaperta), «Richiama ora» si riconosce dal suo dettaglio.
    callingAgain: callingAgain || (save.state === "saving" && save.input.detail === "call_again_now"),
  };
  const bookingKind = bookingKindOf(facts);
  const pending = pendingRequiredSteps(facts, done);
  const pendingKey = pending.join(",");
  const { setPendingSteps } = api;
  useEffect(() => {
    setPendingSteps(pendingKey === "" ? [] : pendingKey.split(","));
  }, [pendingKey, setPendingSteps]);

  const steps = AFTER_OUTCOME_STEPS.filter((step) => step.appliesTo(facts)).map((step) => step.id);
  const quickKey = quickStatusKey(facts);
  const suggested = suggestedWhatsApp(facts);
  // Il nome del modello proposto è quello dell'azienda (i modelli si rinominano): la testata li legge già quando «WhatsApp» è acceso.
  const templates = useQuery({ ...whatsAppTemplatesQuery(tab), enabled: steps.includes("whatsapp") });
  const suggestedName = templates.data?.templates.find((template) => template.key === suggested)?.name ?? null;
  const whatsAppWhy = !card.whatsapp.available && card.whatsapp.reason !== null ? unavailableReasonText(t, card, card.whatsapp.reason) : null;
  const quick = quickKey === null ? undefined : choices.find((status) => status.key === quickKey);

  function choose(next: Partial<typeof draft>) {
    setProblem(null);
    setDraft((previous) => ({ ...previous, ...next }));
    // Scelto «Richiamare», gli orari si aprono da soli (product owner, 27 settembre 2026).
    if (next.answeredOutcome === "call_back" && facts.canSetCallback && !callbackDone) setCallbackOpen(true);
    // Scelta «Disco prenotata» o «Demo prenotata», la prenotazione si apre da sola, come gli orari dopo «Richiamare».
    if (bookingKindOf({ outcome: "answered", answeredOutcome: next.answeredOutcome ?? null }) !== null && facts.canBook === true && !bookingDone) setBookingOpen(true);
  }

  /** L'esito della postazione, oppure il motivo per cui ancora non c'è. */
  function outcomeInput(): OutcomeInput | null {
    const text = note.trim() === "" ? null : note.trim();
    if (chosen.outcome === null) return fail("callDesk.reasonNoOutcome");
    if (chosen.outcome === "answered" && chosen.answeredOutcome === null) return fail("callDesk.reasonNoAnswered");
    // La nota obbligatoria con «Altro»: proposta di Claude, come il `422 validation_failed` (motivo `note_required`) del server (questione 215).
    if (chosen.answeredOutcome === "other" && text === null) return fail("callDesk.reasonNoNote");
    return { outcome: chosen.outcome, answeredOutcome: chosen.outcome === "answered" ? chosen.answeredOutcome : null, detail: null, note: text };
  }

  function fail(key: MessageKey): null {
    setProblem(key);
    return null;
  }

  /** L'esito salvato prima di andare avanti; `null` se non c'è ancora o il server lo rifiuta (lo dice la postazione). */
  async function ensureSaved(): Promise<OutcomeSaved | null> {
    if (save.state === "saved") return { late: save.late, call: save.call };
    const input = outcomeInput();
    if (!input) return null;
    try {
      return await api.saveOutcome(input);
    } catch {
      return null;
    }
  }

  async function finish() {
    const result = await ensureSaved();
    // Un esito già salvato altrove si dice prima di chiudere.
    if (!result || (result.late && save.state !== "saved")) return;
    // I passi con la chiamata salvata: la durata che il server dà con l'esito vale più di quella del browser.
    let after = { ...facts, talkSeconds: talkSecondsOf(session, result.call) };
    const writing = statusWrite.current;
    if (writing) {
      // Lo status in volo decide se il passo dello status è fatto: si aspetta la risposta del server. Rifiutato, lo status
      // resta quello di prima e, se il passo è obbligatorio, la postazione resta.
      const written = await writing;
      if (written) after = { ...after, leadStatusIsDefault: written.lead.status.isDefault };
    }
    const steps = pendingRequiredSteps(after, done);
    api.setPendingSteps(steps);
    // Manca l'orario del richiamo: la finestra si apre di nuovo, la postazione resta.
    if (steps.includes("callback")) {
      setCallbackOpen(true);
      return;
    }
    if (steps.includes("booking")) {
      setBookingOpen(true);
      return;
    }
    api.close();
  }

  /** L'esito salvato prima di fissare il richiamo dalla postazione; `false` se manca o il server lo rifiuta (lo dice la postazione). */
  async function saveBeforeCallback(): Promise<boolean> {
    const result = await ensureSaved();
    return result !== null;
  }

  function appointmentBooked(appointment: AppointmentView) {
    setBooked(appointment);
    api.markStepDone("booking");
  }

  function callbackSaved(task: TaskView) {
    setFixed(task);
    api.markStepDone("callback");
  }

  async function changeStatus(id: string) {
    const status = choices.find((candidate) => candidate.id === id);
    if (!status || status.id === lead.status.id) return;
    if (!(await ensureSaved())) return;
    const write = writes.save(statusEdit(status)).then(
      (response) => {
        toast.show(t("leadCard.statusSaved", { name: status.name }), "success");
        return response;
      },
      (error: unknown) => {
        if (isCardLevelError(error)) onCardError(error);
        else toast.show(messageForError(t, error), "warning");
        return null;
      },
    );
    statusWrite.current = write;
    void write.finally(() => {
      if (statusWrite.current === write) statusWrite.current = null;
    });
  }

  /** «Correggi il numero»: prima l'esito, che segna errato il numero chiamato, poi la casella del numero nei dati. */
  async function fixPhone() {
    if (phone === null || !(await ensureSaved())) return;
    openPhoneField(phone);
  }

  /**
   * «Invia WhatsApp», dal passo o da sotto i passi: prima l'esito (agisce fuori dalla postazione), poi la finestra con il modello
   * proposto dopo l'esito, se c'è (altrimenti il primo dell'azienda). Un esito già salvato altrove si dice prima, e i passi
   * diventano i suoi.
   */
  async function sendWhatsApp(template: WhatsAppTemplateKeyCode | null) {
    const result = await ensureSaved();
    if (!result || (result.late && save.state !== "saved")) return;
    setWhatsAppOpen({ template: template ?? undefined });
  }

  /**
   * «Richiama ora»: «Nessuna risposta» con `call_again_now`, poi una chiamata nuova allo stesso telefono. Se la chiamata
   * aveva già un esito, resta quello, con i suoi passi e l'avviso, e non si richiama.
   */
  async function callAgainNow() {
    setProblem(null);
    if (phone === null) return;
    setCallingAgain(true);
    let saved;
    try {
      saved = await api.saveOutcome({ outcome: "no_answer", answeredOutcome: null, detail: "call_again_now", note: note.trim() === "" ? null : note.trim() });
    } catch {
      setCallingAgain(false);
      return;
    }
    if (saved.late) {
      setCallingAgain(false);
      return;
    }
    // I passi obbligatori di «Nessuna risposta», dalla stessa regola: se ce ne fossero, la postazione resta.
    api.setPendingSteps(pendingRequiredSteps({ ...facts, outcome: "no_answer", answeredOutcome: null }, done));
    if (!api.close()) return;
    const { target, leadName } = session;
    await api.start({ tab: target.tab, leadId: target.leadId, phone, leadName, ...(target.dialerSessionId === null ? {} : { dialerSessionId: target.dialerSessionId }) });
  }

  const statusPending = save.state === "saved" && pending.includes("status");
  // «Richiamo fissato per …»: il richiamo fissato qui, oppure quello aperto della scheda (la scheda lasciata e riaperta).
  const fixedAt = fixed?.dueAt ?? card.openTask?.dueAt ?? null;
  const offerLater = chosen.outcome !== null && facts.canSetCallback && !callbackDone;
  // «Invia WhatsApp» sotto i passi, per gli esiti senza il passo: le stesse regole del passo, senza il modello proposto.
  const offerWhatsApp =
    chosen.outcome !== null && facts.canOfferWhatsApp && !facts.whatsAppPrepared && !facts.callingAgain && !steps.includes("whatsapp");
  return (
    <div className="g-desk__outcome">
      <RadioGroup
        name={`outcome-${session.call.id}`}
        legend={t("callDesk.outcomeLegend")}
        look="tiles"
        disabled={locked}
        leading={
          phone !== null ? (
            <Button className="g-desk__again" disabled={locked} onClick={() => void callAgainNow()}>
              <Icon name="phone" />
              {t("callDesk.callAgainNow")}
            </Button>
          ) : null
        }
        options={FIRST_STEP.map((option) => ({ value: option.value, label: t(option.label) }))}
        value={chosen.outcome}
        onChange={(outcome) => choose({ outcome, answeredOutcome: outcome === "answered" ? draft.answeredOutcome : null })}
      />
      {chosen.outcome === "answered" ? (
        <RadioGroup
          name={`answered-${session.call.id}`}
          legend={t("callDesk.howDidItGo")}
          look="tiles"
          disabled={locked}
          options={ANSWERED_OUTCOMES.map((value) => ({ value, label: t(`answeredOutcomes.${value}`) }))}
          value={chosen.answeredOutcome}
          onChange={(answeredOutcome) => choose({ answeredOutcome })}
        />
      ) : null}
      <TextArea
        id={notesId}
        label={t("callDesk.notesLabel")}
        className="g-desk__notes"
        rows={2}
        placeholder={t("callDesk.notesPlaceholder")}
        value={save.state === "saving" ? (save.input.note ?? "") : note}
        maxLength={4000}
        disabled={locked}
        onChange={(event) => {
          setProblem(null);
          setNote(event.target.value);
        }}
      />
      {steps.length > 0 && chosen.outcome !== null ? (
        <section className="g-desk__steps" aria-label={t("callDesk.afterOutcome", { outcome: t(`outcomes.${chosen.outcome}`) })}>
          {steps.includes("callback") ? (
            <div className="g-desk__step" role="group" aria-labelledby={callbackQuestionId}>
              <p id={callbackQuestionId} className="g-desk__question">
                {t("callbacks.step")}
              </p>
              {callbackDone ? (
                fixedAt ? (
                  <p className="g-desk__hint">{t("callbacks.stepDone", { when: formatNumericDateTime(timeZone, fixedAt) })}</p>
                ) : null
              ) : (
                <div className="g-desk__row">
                  <Button variant="primary" aria-haspopup="dialog" onClick={() => setCallbackOpen(true)}>
                    <Icon name="clock" />
                    {t("callbacks.stepOpen")}
                  </Button>
                </div>
              )}
            </div>
          ) : null}
          {steps.includes("booking") ? (
            <div className="g-desk__step" role="group" aria-labelledby={bookingQuestionId}>
              <p id={bookingQuestionId} className="g-desk__question">
                {t("appointments.step.question", { kind: t(`appointments.kind.${bookingKind ?? "disco"}`) })}
              </p>
              {bookingDone ? (
                booked ? (
                  <p className="g-desk__hint">{t("appointments.step.done", { kind: t(`appointments.kind.${booked.kind}`), when: formatNumericDateTime(timeZone, booked.startsAt) })}</p>
                ) : null
              ) : (
                <div className="g-desk__row">
                  <Button variant="primary" aria-haspopup="dialog" onClick={() => setBookingOpen(true)}>
                    <Icon name="calendar" />
                    {t("appointments.step.open")}
                  </Button>
                </div>
              )}
            </div>
          ) : null}
          {steps.includes("search") ? (
            <div className="g-desk__step">
              <p className="g-desk__hint">{t("callDesk.searchHint")}</p>
              <div className="g-desk__row">
                <a className="g-desk__link" href={searchUrl(card)} target="_blank" rel="noopener noreferrer">
                  <Icon name="search" />
                  {t("callDesk.searchSalon")}
                </a>
                {steps.includes("fix_phone") ? <Button onClick={() => void fixPhone()}>{t("callDesk.fixPhone")}</Button> : null}
              </div>
            </div>
          ) : null}
          {steps.includes("whatsapp") && suggested !== null ? (
            <div className="g-desk__step" role="group" aria-labelledby={whatsAppQuestionId}>
              <p id={whatsAppQuestionId} className="g-desk__question">
                {suggestedName === null ? t("whatsapp.step.questionAny") : t("whatsapp.step.question", { template: suggestedName })}
              </p>
              <div className="g-desk__row">
                <Button
                  aria-haspopup="dialog"
                  disabled={!card.whatsapp.available}
                  aria-describedby={whatsAppWhy ? whatsAppReasonId : undefined}
                  onClick={() => void sendWhatsApp(suggested)}
                >
                  <Icon name="whatsapp" />
                  {t("whatsapp.step.open")}
                </Button>
              </div>
              {whatsAppWhy ? (
                <Reason tone="warning" id={whatsAppReasonId}>
                  {whatsAppWhy}
                </Reason>
              ) : null}
            </div>
          ) : null}
          {steps.includes("status") ? (
            <div className="g-desk__step" role="group" aria-labelledby={statusQuestionId}>
              <p id={statusQuestionId} className="g-desk__question">
                {t("callDesk.statusQuestion")}
              </p>
              <p className="g-desk__hint">{t("callDesk.statusNow", { status: lead.status.name })}</p>
              <div className="g-desk__row">
                {quick && quick.id !== lead.status.id ? (
                  <Button variant="primary" disabled={!card.permissions.canUpdate} onClick={() => void changeStatus(quick.id)}>
                    {t("callDesk.quickStatus", { status: quick.name })}
                  </Button>
                ) : null}
                {statuses.isError && card.permissions.canUpdate ? (
                  <>
                    <Reason tone="warning" role="alert">
                      {t("callDesk.statusesUnavailable")}
                    </Reason>
                    <Button disabled={statuses.isFetching} onClick={() => void statuses.refetch()}>
                      {t("common.retry")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Select
                      id={statusSelectId}
                      label={t("callDesk.newStatus")}
                      hideLabel
                      value={chosenStatus}
                      disabled={!card.permissions.canUpdate || !statuses.isSuccess}
                      onChange={(to) => setStatusDraft({ from: lead.status.id, to })}
                      options={[
                        ...(choices.some((status) => status.id === lead.status.id) ? [] : [{ value: lead.status.id, label: lead.status.name }]),
                        ...choices.map((status) => ({ value: status.id, label: status.name })),
                      ]}
                    />
                    <Button disabled={!card.permissions.canUpdate || chosenStatus === lead.status.id} onClick={() => void changeStatus(chosenStatus)}>
                      {t("callDesk.saveStatus")}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
      {offerLater || offerWhatsApp ? (
        <div className="g-desk__row">
          {offerLater ? (
            <Button aria-haspopup="dialog" onClick={() => setCallbackOpen(true)}>
              <Icon name="clock" />
              {t("callbacks.later")}
            </Button>
          ) : null}
          {offerWhatsApp ? (
            <Button
              aria-haspopup="dialog"
              disabled={!card.whatsapp.available}
              aria-describedby={whatsAppWhy ? whatsAppLaterReasonId : undefined}
              onClick={() => void sendWhatsApp(suggested)}
            >
              <Icon name="whatsapp" />
              {t("whatsapp.step.open")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {offerWhatsApp && whatsAppWhy ? (
        <Reason tone="warning" id={whatsAppLaterReasonId}>
          {whatsAppWhy}
        </Reason>
      ) : null}
      <SaveState save={save} />
      {problem ? (
        <Reason tone="warning" role="alert">
          {t(problem)}
        </Reason>
      ) : statusPending ? (
        <Reason tone="warning" role="alert">
          {t("callDesk.reasonStatus", { minutes: STATUS_REQUIRED_AFTER_SECONDS / 60, status: lead.status.name })}
        </Reason>
      ) : null}
      <div className="g-desk__footer">
        <Button variant="primary" disabled={save.state === "saving"} onClick={() => void finish()}>
          {t("callDesk.close")}
        </Button>
      </div>
      {callbackOpen ? (
        <CallbackDialog
          tab={tab}
          card={card}
          timeZone={timeZone}
          fromCall={{ callId: session.call.id, startedAt: session.call.startedAt, beforeSave: saveBeforeCallback }}
          onSaved={callbackSaved}
          onClose={() => setCallbackOpen(false)}
        />
      ) : null}
      {bookingOpen ? (
        <BookAppointmentDialog
          tab={tab}
          leadId={lead.id}
          timeZone={timeZone}
          initialKind={bookingKind ?? "disco"}
          callId={session.call.id}
          beforeSave={saveBeforeCallback}
          onSaved={appointmentBooked}
          onClose={() => setBookingOpen(false)}
        />
      ) : null}
      {whatsAppOpen ? (
        <WhatsAppDialog
          tab={tab}
          leadId={lead.id}
          callId={session.call.id}
          suggestedTemplate={whatsAppOpen.template}
          onPrepared={() => api.markStepDone("whatsapp")}
          onClose={() => setWhatsAppOpen(null)}
        />
      ) : null}
    </div>
  );
}

/** Come va il salvataggio dell'esito: in corso, in attesa di un nuovo tentativo, rifiutato, arrivato tardi. */
function SaveState({ save }: { save: Extract<CallSession, { phase: "outcome" }>["save"] }) {
  const t = useT();
  if (save.state === "saving") return <p role="status" className="g-desk__hint">{t(save.retrying ? "callDesk.retrying" : "callDesk.saving")}</p>;
  if (save.state === "failed") return <Reason tone="warning" role="alert">{messageForError(t, save.error)}</Reason>;
  if (save.state === "saved" && save.late) return <Reason tone="warning" role="alert">{t("errors.call_outcome_exists")}</Reason>;
  return null;
}

/** La ricerca del salone su Google: nome del salone (o del Lead) e città. */
function searchUrl(card: LeadCardResponse): string {
  const { lead } = card;
  const name = lead.salonName ?? [lead.firstName, lead.lastName].filter(Boolean).join(" ");
  const query = [name, lead.place?.cityName ?? null].filter((part): part is string => Boolean(part)).join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}
